import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ChevronDown, ChevronUp, ChevronsUpDown, ListFilter } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../utils';
import { EmptyState, ErrorState, LoadingState } from '../States';
import { BulkActionsBar } from './BulkActionsBar';
import { DataTablePagination } from './DataTablePagination';
import { DataTableToolbar } from './DataTableToolbar';
import { FilterPopover } from './FilterPopover';
import { SortableRow } from './SortableRow';
import type {
  BulkAction,
  ColumnDef,
  ColumnFilter,
  ColumnFilters,
  DataTableState,
  Density,
  PageSize,
  RowDragConfig,
  SortDir,
  SortState,
} from './types';
import { PAGE_SIZES } from './types';

/** Minimal Supabase-compatible auth interface — avoids circular dep on @sazeo/sdk. */
interface SupabaseClientLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

/** Persisted per-table user preferences. */
interface TablePrefs {
  sort?: SortState | null;
  pageSize?: PageSize;
  density?: Density;
  columnFilters?: ColumnFilters;
  hiddenColumnIds?: string[];
}

export interface DataTableProps<T> {
  /** Columns. Left-most should usually have `sticky: 'left'` (merged ID + actions). */
  columns: ColumnDef<T>[];
  /** Row data. Full set; DataTable handles pagination + search in-memory. */
  data: T[];
  /** Stable row identifier — used for selection + keying. */
  getRowId?: (row: T) => string;

  /** Overall state: idle / loading / empty / error. Default: idle (auto-empty if data is empty). */
  state?: DataTableState;
  /** Error body (shown when state === 'error'). */
  error?: ReactNode;
  /** Empty-state override. */
  empty?: { title?: ReactNode; description?: ReactNode; action?: ReactNode; icon?: ReactNode };

  /** Enable row selection column. Default: false. */
  selectable?: boolean;
  /** Legacy selection callback kept for older feature pages. */
  onSelectionChange?: (ids: string[]) => void;
  /** Bulk actions (only shown when selectable + selectedCount > 0). */
  bulkActions?: BulkAction<T>[];
  /** Row click handler. */
  onRowClick?: (row: T) => void;

  /** Controlled state — if supplied, DataTable is controlled. */
  sort?: SortState | null;
  onSortChange?: (s: SortState | null) => void;
  page?: number;
  onPageChange?: (p: number) => void;
  pageSize?: PageSize;
  onPageSizeChange?: (s: PageSize) => void;
  density?: Density;
  onDensityChange?: (d: Density) => void;
  /** Legacy boolean density shortcut. false maps to comfortable, true maps to compact. */
  dense?: boolean;
  search?: string;
  onSearchChange?: (v: string) => void;
  /** Legacy feature toggles. New DataTable keeps search/sort enabled by column config. */
  searchable?: boolean;
  sortable?: boolean;

  /** Initial uncontrolled values (only used if the controlled prop is undefined). */
  initialSort?: SortState | null;
  initialPageSize?: PageSize;
  initialDensity?: Density;

  /** Filter predicate used when `search` is non-empty. Default: stringified cell scan. */
  searchPredicate?: (row: T, q: string) => boolean;

  /** Text shown in the search box. */
  searchPlaceholder?: string;

  /** Extra nodes rendered in toolbar zone #4 (right side). */
  toolbarRightSlot?: ReactNode;

  /** Hide the toolbar (e.g., when the page provides its own). Default: false. */
  hideToolbar?: boolean;
  /** Hide pagination (e.g., infinite-scroll use case). */
  hidePagination?: boolean;

  /** Custom className on outer wrapper. */
  className?: string;
  /** Optional test id for integration tests. */
  testId?: string;
  /** Per-row data-testid generator. Receives the row object; return a string testid. */
  getRowTestId?: (row: T) => string;
  /** Max height; enables sticky header + scroll. */
  maxHeight?: string;

  /**
   * Opt-in row drag-to-reorder. When enabled a GripVertical handle column is
   * prepended. Optimistic reorder happens immediately; caller persists via onReorder.
   */
  rowDrag?: RowDragConfig<T>;

  /** Controlled column filters. */
  columnFilters?: ColumnFilters;
  onColumnFiltersChange?: (filters: ColumnFilters) => void;
  /** Initial uncontrolled column filters. */
  initialColumnFilters?: ColumnFilters;

  /**
   * Controlled column visibility. Set of column ids that are hidden.
   * When omitted DataTable manages it internally (uncontrolled).
   */
  hiddenColumnIds?: ReadonlySet<string>;
  onHiddenColumnIdsChange?: (ids: Set<string>) => void;
  /** Initial hidden column ids for uncontrolled mode. Column-level `hidden: true` seeds this. */
  initialHiddenColumnIds?: string[];

  /**
   * Opt-in persistence. When both props are provided, sort / pageSize /
   * density / columnFilters / hiddenColumnIds are restored from user_preferences
   * on mount and auto-saved on change. No-op when either is omitted.
   */
  tableId?: string;
  supabase?: SupabaseClientLike;
}

const DENSITY_PAD: Record<Density, string> = {
  compact: 'h-8 px-2',
  comfortable: 'h-10 px-3',
  spacious: 'h-12 px-4',
};

const DENSITY_PAD_HEAD: Record<Density, string> = {
  compact: 'h-8 px-2',
  comfortable: 'h-10 px-3',
  spacious: 'h-11 px-4',
};

function defaultAccessor<T>(col: ColumnDef<T>, row: T): unknown {
  if (col.accessor) return col.accessor(row);
  // Fallback — try col.id as property key.
  return (row as unknown as Record<string, unknown>)[getColumnId(col)];
}

function getColumnId<T>(col: ColumnDef<T>): string {
  return col.id ?? String((col as unknown as { key?: string }).key ?? '');
}

function getColumnHeader<T>(col: ColumnDef<T>): ReactNode {
  return col.header ?? (col as unknown as { label?: ReactNode }).label ?? getColumnId(col);
}

function getDefaultRowId<T>(row: T): string {
  const id = (row as unknown as { id?: string | number }).id;
  return id == null ? JSON.stringify(row) : String(id);
}

function renderColumn<T>(col: ColumnDef<T>, row: T, rowIdx: number): ReactNode {
  const legacyCol = col as unknown as {
    key?: string;
    render?: (value: unknown, row: T, index: number) => ReactNode;
  };
  if (legacyCol.key && !col.id && legacyCol.render) {
    return legacyCol.render(defaultAccessor(col, row), row, rowIdx);
  }
  return col.render ? col.render(row, rowIdx) : String(defaultAccessor(col, row) ?? '');
}

function compareVals(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b));
}

function defaultSearchPredicate<T>(columns: ColumnDef<T>[], row: T, q: string): boolean {
  const ql = q.toLowerCase();
  for (const col of columns) {
    const v = defaultAccessor(col, row);
    if (v == null) continue;
    if (String(v).toLowerCase().includes(ql)) return true;
  }
  return false;
}

function matchesFilter<T>(col: ColumnDef<T>, row: T, filter: ColumnFilter): boolean {
  const raw = col.accessor ? col.accessor(row) : (row as Record<string, unknown>)[getColumnId(col)];
  switch (filter.type) {
    case 'text':
      return (
        filter.value === '' ||
        String(raw ?? '')
          .toLowerCase()
          .includes(filter.value.toLowerCase())
      );
    case 'select':
      return filter.values.length === 0 || filter.values.includes(String(raw ?? ''));
    case 'range': {
      const n = Number(raw);
      if (filter.min !== null && n < filter.min) return false;
      if (filter.max !== null && n > filter.max) return false;
      return true;
    }
    case 'date': {
      const d = String(raw ?? '').slice(0, 10);
      if (filter.from && d < filter.from) return false;
      if (filter.to && d > filter.to) return false;
      return true;
    }
  }
}

function hasActiveValue(filter: ColumnFilter): boolean {
  switch (filter.type) {
    case 'text':
      return filter.value !== '';
    case 'select':
      return filter.values.length > 0;
    case 'range':
      return filter.min !== null || filter.max !== null;
    case 'date':
      return filter.from !== null || filter.to !== null;
  }
}

// ---------------------------------------------------------------------------
// Persistence helpers (inline — @sazeo/ui has no @tanstack/react-query dep)
// ---------------------------------------------------------------------------

const PREF_BASE = '/functions/v1/settings/preferences';

async function loadTablePrefs(
  supabase: SupabaseClientLike,
  tableId: string,
): Promise<TablePrefs | undefined> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token ?? '';
    const res = await fetch(PREF_BASE, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) return undefined;
    const map = (await res.json()) as Record<string, unknown>;
    return (map[`table:${tableId}`] as TablePrefs | undefined) ?? undefined;
  } catch {
    return undefined;
  }
}

async function saveTablePrefs(
  supabase: SupabaseClientLike,
  tableId: string,
  prefs: TablePrefs,
): Promise<void> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token ?? '';
    await fetch(`${PREF_BASE}/${encodeURIComponent(`table:${tableId}`)}`, {
      method: 'PATCH',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ value: prefs }),
    });
  } catch {
    // silent — no toast per spec
  }
}

export function DataTable<T>(props: DataTableProps<T>) {
  const {
    columns,
    data,
    getRowId,
    state,
    error,
    empty,
    selectable = false,
    onSelectionChange,
    bulkActions = [],
    onRowClick,
    sort: controlledSort,
    onSortChange,
    page: controlledPage,
    onPageChange,
    pageSize: controlledPageSize,
    onPageSizeChange,
    density: controlledDensity,
    onDensityChange,
    dense,
    search: controlledSearch,
    onSearchChange,
    searchable: _searchable,
    sortable: _sortable,
    initialSort = null,
    initialPageSize = 50,
    initialDensity = dense ? 'compact' : 'comfortable',
    searchPredicate,
    searchPlaceholder,
    toolbarRightSlot,
    hideToolbar = false,
    hidePagination = false,
    className,
    testId,
    maxHeight,
    getRowTestId,
    rowDrag,
    columnFilters: controlledColumnFilters,
    onColumnFiltersChange,
    initialColumnFilters,
    hiddenColumnIds: controlledHiddenColumnIds,
    onHiddenColumnIdsChange,
    initialHiddenColumnIds,
    tableId,
    supabase,
  } = props;
  const rowId = getRowId ?? getDefaultRowId;

  // Seed uncontrolled hidden ids from: initialHiddenColumnIds prop OR columns marked hidden:true.
  const defaultHiddenIds = useMemo(() => {
    if (initialHiddenColumnIds) return new Set(initialHiddenColumnIds);
    return new Set(columns.filter((c) => c.hidden).map((c) => getColumnId(c)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps — intentionally only once

  // Internal uncontrolled state.
  const [uSort, setUSort] = useState<SortState | null>(initialSort);
  const [uPage, setUPage] = useState(0);
  const [uPageSize, setUPageSize] = useState<PageSize>(initialPageSize);
  const [uDensity, setUDensity] = useState<Density>(initialDensity);
  const [uSearch, setUSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [uColumnFilters, setUColumnFilters] = useState<ColumnFilters>(initialColumnFilters ?? {});
  const [uHiddenColumnIds, setUHiddenColumnIds] = useState<Set<string>>(defaultHiddenIds);

  // ---------------------------------------------------------------------------
  // Persistence — restore on mount, auto-save on state change (opt-in via tableId+supabase)
  // ---------------------------------------------------------------------------
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  useEffect(() => {
    if (!supabase || !tableId) return;
    void loadTablePrefs(supabase, tableId).then((prefs) => {
      if (!prefs) return;
      if (prefs.sort !== undefined && controlledSort === undefined) setUSort(prefs.sort ?? null);
      if (prefs.pageSize !== undefined && controlledPageSize === undefined)
        setUPageSize(prefs.pageSize);
      if (prefs.density !== undefined && controlledDensity === undefined)
        setUDensity(prefs.density);
      if (prefs.columnFilters !== undefined && controlledColumnFilters === undefined)
        setUColumnFilters(prefs.columnFilters);
      if (prefs.hiddenColumnIds !== undefined && controlledHiddenColumnIds === undefined)
        setUHiddenColumnIds(new Set(prefs.hiddenColumnIds));
    });
  }, [
    supabase,
    tableId,
    controlledSort,
    controlledPageSize,
    controlledDensity,
    controlledColumnFilters,
    controlledHiddenColumnIds,
  ]);

  // Popover anchor: which column's filter icon was clicked.
  const [openFilterColId, setOpenFilterColId] = useState<string | null>(null);
  const filterAnchorRef = useRef<HTMLElement | null>(null);
  // Optimistic drag-reorder state: when non-null, overrides `data` ordering for the current page.
  const [dragOrder, setDragOrder] = useState<T[] | null>(null);

  const dragEnabled = rowDrag?.enabled === true;

  // Auto-save on state change — debounced 500ms. Skipped on the initial mount
  // cycle so we don't immediately overwrite restored preferences with defaults.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (!supabase || !tableId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    retryCountRef.current = 0;

    const snapshot: TablePrefs = {
      sort: uSort,
      pageSize: uPageSize,
      density: uDensity,
      columnFilters: uColumnFilters,
      hiddenColumnIds: Array.from(uHiddenColumnIds),
    };

    const attempt = async () => {
      try {
        await saveTablePrefs(supabase, tableId, snapshot);
        retryCountRef.current = 0;
      } catch {
        retryCountRef.current += 1;
        if (retryCountRef.current < 3) {
          saveTimerRef.current = setTimeout(() => void attempt(), 1500 * retryCountRef.current);
        }
      }
    };

    saveTimerRef.current = setTimeout(() => void attempt(), 500);
  }, [uSort, uPageSize, uDensity, uColumnFilters, uHiddenColumnIds, supabase, tableId]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const sort = controlledSort !== undefined ? controlledSort : uSort;
  const page = controlledPage !== undefined ? controlledPage : uPage;
  const pageSize = controlledPageSize !== undefined ? controlledPageSize : uPageSize;
  const density = controlledDensity !== undefined ? controlledDensity : uDensity;
  const search = controlledSearch !== undefined ? controlledSearch : uSearch;
  const columnFilters =
    controlledColumnFilters !== undefined ? controlledColumnFilters : uColumnFilters;
  const hiddenColumnIds =
    controlledHiddenColumnIds !== undefined ? controlledHiddenColumnIds : uHiddenColumnIds;

  const setHiddenColumnIds = useCallback(
    (ids: Set<string>) => {
      if (onHiddenColumnIdsChange) onHiddenColumnIdsChange(ids);
      if (controlledHiddenColumnIds === undefined) setUHiddenColumnIds(ids);
    },
    [onHiddenColumnIdsChange, controlledHiddenColumnIds],
  );

  const toggleColumnVisibility = useCallback(
    (columnId: string) => {
      const next = new Set(hiddenColumnIds);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      setHiddenColumnIds(next);
    },
    [hiddenColumnIds, setHiddenColumnIds],
  );

  const setSort = useCallback(
    (s: SortState | null) => {
      if (onSortChange) onSortChange(s);
      if (controlledSort === undefined) setUSort(s);
    },
    [onSortChange, controlledSort],
  );
  const setPage = useCallback(
    (p: number) => {
      if (onPageChange) onPageChange(p);
      if (controlledPage === undefined) setUPage(p);
    },
    [onPageChange, controlledPage],
  );
  const setPageSize = useCallback(
    (s: PageSize) => {
      if (onPageSizeChange) onPageSizeChange(s);
      if (controlledPageSize === undefined) setUPageSize(s);
      setPage(0);
    },
    [onPageSizeChange, controlledPageSize, setPage],
  );
  const setDensity = useCallback(
    (d: Density) => {
      if (onDensityChange) onDensityChange(d);
      if (controlledDensity === undefined) setUDensity(d);
    },
    [onDensityChange, controlledDensity],
  );
  const setSearch = useCallback(
    (v: string) => {
      if (onSearchChange) onSearchChange(v);
      if (controlledSearch === undefined) setUSearch(v);
      setPage(0);
    },
    [onSearchChange, controlledSearch, setPage],
  );

  const setColumnFilters = useCallback(
    (filters: ColumnFilters) => {
      if (onColumnFiltersChange) onColumnFiltersChange(filters);
      if (controlledColumnFilters === undefined) setUColumnFilters(filters);
    },
    [onColumnFiltersChange, controlledColumnFilters],
  );

  const visibleCols = useMemo(
    () => columns.filter((c) => !hiddenColumnIds.has(getColumnId(c))),
    [columns, hiddenColumnIds],
  );

  // Filtered rows.
  const filtered = useMemo(() => {
    let rows = data;
    // 1. Full-text search
    if (search) {
      const pred =
        searchPredicate ?? ((row: T, q: string) => defaultSearchPredicate(visibleCols, row, q));
      rows = rows.filter((row) => pred(row, search));
    }
    // 2. Column filters (AND logic)
    for (const [colId, filter] of Object.entries(columnFilters)) {
      if (!hasActiveValue(filter)) continue;
      const col = visibleCols.find((c) => getColumnId(c) === colId);
      if (!col) continue;
      rows = rows.filter((row) => matchesFilter(col, row, filter));
    }
    return rows;
  }, [data, search, columnFilters, searchPredicate, visibleCols]);

  // Sorted rows.
  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = visibleCols.find((c) => getColumnId(c) === sort.columnId);
    if (!col) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort(
      (a, b) => dir * compareVals(defaultAccessor(col, a), defaultAccessor(col, b)),
    );
  }, [filtered, sort, visibleCols]);

  // Drag-override data: when dragOrder is set (optimistic), use it instead of `data`.
  const effectiveData = dragOrder ?? data;

  // Paginated view.
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = hidePagination
    ? sorted
    : sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);

  // The page rows after applying the drag order override.
  // When drag is active we rebuild pageRows from effectiveData preserving filter/sort.
  const effectivePageRows = useMemo(() => {
    if (!dragOrder) return pageRows;
    if (!search && !sort) {
      // Fast path: no filter/sort, just re-slice effectiveData.
      const count = hidePagination ? effectiveData.length : pageSize;
      const start = hidePagination ? 0 : safePage * pageSize;
      return effectiveData.slice(start, start + count);
    }
    return pageRows;
  }, [dragOrder, pageRows, search, sort, effectiveData, hidePagination, pageSize, safePage]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !rowDrag?.onReorder) return;

      // Find positions within the current page view.
      const ids = effectivePageRows.map((r) => rowId(r));
      const fromIdx = ids.indexOf(String(active.id));
      const toIdx = ids.indexOf(String(over.id));
      if (fromIdx === -1 || toIdx === -1) return;

      const movedRow = effectivePageRows[fromIdx];
      if (!movedRow) return;

      // Apply optimistic reorder on the full dataset.
      const globalFrom = effectiveData.findIndex((r) => rowId(r) === String(active.id));
      const globalTo = effectiveData.findIndex((r) => rowId(r) === String(over.id));
      const newOrder = arrayMove(effectiveData, globalFrom, globalTo);
      setDragOrder(newOrder);

      // Notify caller — let it persist and optionally roll back.
      void rowDrag.onReorder(newOrder, { from: fromIdx, to: toIdx, row: movedRow });
    },
    [effectivePageRows, effectiveData, rowId, rowDrag],
  );

  const toggleSort = useCallback(
    (col: ColumnDef<T>) => {
      if (!col.sortable) return;
      const columnId = getColumnId(col);
      if (!sort || sort.columnId !== columnId) {
        setSort({ columnId, dir: 'asc' });
      } else if (sort.dir === 'asc') {
        setSort({ columnId, dir: 'desc' });
      } else {
        setSort(null);
      }
    },
    [sort, setSort],
  );

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const allIds = pageRows.map((r) => rowId(r));
      const allOnPageSelected = allIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allOnPageSelected) {
        for (const id of allIds) next.delete(id);
      } else {
        for (const id of allIds) next.add(id);
      }
      return next;
    });
  }, [pageRows, rowId]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const selectedRows = useMemo(
    () => data.filter((r) => selected.has(rowId(r))),
    [data, selected, rowId],
  );
  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const selectedIdsKey = selectedIds.join('\u0000');
  const onSelectionChangeRef = useRef(onSelectionChange);
  const lastSelectionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  useEffect(() => {
    if (!onSelectionChangeRef.current) return;
    if (lastSelectionKeyRef.current === selectedIdsKey) return;
    lastSelectionKeyRef.current = selectedIdsKey;
    onSelectionChangeRef.current(selectedIds);
  }, [selectedIds, selectedIdsKey]);

  // Active column filter count (for toolbar badge)
  const activeFilterCount = Object.values(columnFilters).filter(hasActiveValue).length;

  const clearAllFilters = useCallback(() => {
    setColumnFilters({});
  }, [setColumnFilters]);

  // Derived state.
  const derivedState: DataTableState = state ?? (data.length === 0 ? 'empty' : 'idle');
  const isLoading = derivedState === 'loading';
  const isError = derivedState === 'error';
  const isEmpty = derivedState === 'empty' || (!isLoading && !isError && filtered.length === 0);

  const wrapperCls = cn(
    'flex flex-col w-full',
    'border border-[var(--color-border)] rounded-[4px] overflow-hidden',
    'bg-[var(--color-bg)]',
    className,
  );

  const colSpanTotal = visibleCols.length + (selectable ? 1 : 0) + (dragEnabled ? 1 : 0);

  const tableHead = (
    <thead className="sticky top-0 z-10 bg-[var(--color-bg)]">
      <tr className="border-b border-[var(--color-border)]">
        {dragEnabled ? (
          <th
            scope="col"
            aria-label="drag handle"
            className={cn(DENSITY_PAD_HEAD[density], 'w-8 align-middle')}
          />
        ) : null}
        {selectable ? (
          <th
            scope="col"
            className={cn(
              DENSITY_PAD_HEAD[density],
              'w-10 text-left align-middle',
              'sticky left-0 z-20 bg-[var(--color-bg)]',
            )}
          >
            <input
              type="checkbox"
              aria-label="select all on page"
              checked={
                effectivePageRows.length > 0 &&
                effectivePageRows.every((r) => selected.has(rowId(r)))
              }
              onChange={toggleAll}
              className="accent-[var(--color-accent)]"
            />
          </th>
        ) : null}
        {visibleCols.map((col) => {
          const columnId = getColumnId(col);
          const columnHeader = getColumnHeader(col);
          const isSorted = sort?.columnId === columnId;
          const isFiltered =
            col.filter !== undefined &&
            columnFilters[columnId] !== undefined &&
            hasActiveValue(columnFilters[columnId] as ColumnFilter);
          return (
            <th
              key={columnId}
              scope="col"
              aria-sort={isSorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
              style={col.width ? { width: col.width } : undefined}
              className={cn(
                DENSITY_PAD_HEAD[density],
                'text-left align-middle text-[11px] font-medium uppercase tracking-[0.04em]',
                'text-[var(--color-text-secondary)]',
                col.align === 'right' && 'text-right',
                col.align === 'center' && 'text-center',
                col.sticky === 'left' &&
                  `sticky z-20 bg-[var(--color-bg)] ${selectable ? 'left-10' : 'left-0'}`,
                col.sticky === 'right' && 'sticky right-0 z-20 bg-[var(--color-bg)]',
              )}
            >
              <span className="inline-flex items-center gap-1">
                {col.sortable ? (
                  <button
                    type="button"
                    onClick={() => toggleSort(col)}
                    className={cn(
                      'inline-flex items-center gap-1 uppercase tracking-[0.04em]',
                      'hover:text-[var(--color-text-primary)] transition-colors',
                    )}
                  >
                    {columnHeader}
                    <SortIcon isSorted={isSorted} dir={sort?.dir} />
                  </button>
                ) : (
                  columnHeader
                )}
                {col.filter ? (
                  <button
                    type="button"
                    aria-label={`Filter ${col.label ?? String(columnHeader)}`}
                    aria-pressed={isFiltered}
                    onClick={(e) => {
                      e.stopPropagation();
                      filterAnchorRef.current = e.currentTarget;
                      setOpenFilterColId(openFilterColId === columnId ? null : columnId);
                    }}
                    className={cn(
                      'ml-1 inline-flex items-center justify-center size-4 rounded-[2px]',
                      'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]',
                      'transition-colors',
                      isFiltered && 'text-[var(--color-accent)]',
                    )}
                  >
                    <ListFilter className="size-3.5" aria-hidden />
                  </button>
                ) : null}
              </span>
            </th>
          );
        })}
      </tr>
    </thead>
  );

  function renderCells(row: T, rowIdx: number) {
    const id = rowId(row);
    const isSelected = selected.has(id);
    return (
      <>
        {selectable ? (
          <td
            className={cn(DENSITY_PAD[density], 'align-middle sticky left-0 z-10 bg-[inherit]')}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              aria-label={`select row ${id}`}
              checked={isSelected}
              onChange={() => toggleRow(id)}
              className="accent-[var(--color-accent)]"
            />
          </td>
        ) : null}
        {visibleCols.map((col) => {
          const columnId = getColumnId(col);
          const content = renderColumn(col, row, rowIdx);
          return (
            <td
              key={columnId}
              className={cn(
                DENSITY_PAD[density],
                'align-middle',
                col.align === 'right' && 'text-right',
                col.align === 'center' && 'text-center',
                col.sticky === 'left' &&
                  `sticky z-10 bg-[inherit] ${selectable ? 'left-10' : 'left-0'}`,
                col.sticky === 'right' && 'sticky right-0 z-10 bg-[inherit]',
              )}
              style={col.width ? { width: col.width } : undefined}
            >
              {content}
            </td>
          );
        })}
      </>
    );
  }

  const tableBody = (
    <tbody>
      {isLoading ? (
        <tr>
          <td colSpan={colSpanTotal} className="p-0">
            <LoadingState rows={5} columns={Math.min(4, visibleCols.length)} />
          </td>
        </tr>
      ) : isError ? (
        <tr>
          <td colSpan={colSpanTotal} className="p-0">
            <ErrorState description={error} />
          </td>
        </tr>
      ) : isEmpty ? (
        <tr>
          <td colSpan={colSpanTotal} className="p-0">
            <EmptyState
              title={empty?.title ?? 'No rows'}
              description={empty?.description}
              action={empty?.action}
              icon={empty?.icon}
            />
          </td>
        </tr>
      ) : dragEnabled ? (
        <SortableContext
          items={effectivePageRows.map((r) => rowId(r))}
          strategy={verticalListSortingStrategy}
        >
          {effectivePageRows.map((row, rowIdx) => {
            const id = rowId(row);
            const isSelected = selected.has(id);
            return (
              <SortableRow
                key={id}
                id={id}
                cellPadding={DENSITY_PAD[density]}
                handleLabel="Drag to reorder"
                className={cn(
                  'border-b border-[var(--color-border)] transition-colors',
                  rowIdx % 2 === 0 ? 'bg-[var(--color-bg)]' : 'bg-[var(--color-surface)]',
                  'dark:bg-[var(--color-bg)]',
                  'hover:bg-[var(--color-surface)] dark:hover:bg-[var(--color-surface)]',
                  onRowClick && 'cursor-pointer',
                  isSelected && 'bg-[var(--color-surface)] dark:bg-[var(--color-surface)]',
                )}
              >
                {renderCells(row, rowIdx)}
              </SortableRow>
            );
          })}
        </SortableContext>
      ) : (
        effectivePageRows.map((row, rowIdx) => {
          const id = rowId(row);
          const isSelected = selected.has(id);
          return (
            <tr
              key={id}
              data-testid={getRowTestId ? getRowTestId(row) : undefined}
              data-state={isSelected ? 'selected' : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              tabIndex={onRowClick ? 0 : undefined}
              className={cn(
                'border-b border-[var(--color-border)] transition-colors',
                rowIdx % 2 === 0 ? 'bg-[var(--color-bg)]' : 'bg-[var(--color-surface)]',
                'dark:bg-[var(--color-bg)]',
                'hover:bg-[var(--color-surface)] dark:hover:bg-[var(--color-surface)]',
                onRowClick && 'cursor-pointer',
                isSelected && 'bg-[var(--color-surface)] dark:bg-[var(--color-surface)]',
              )}
            >
              {renderCells(row, rowIdx)}
            </tr>
          );
        })
      )}
    </tbody>
  );

  return (
    <div className={wrapperCls} data-testid={testId}>
      {!hideToolbar ? (
        <DataTableToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={searchPlaceholder}
          density={density}
          onDensityChange={setDensity}
          total={data.length}
          filtered={filtered.length}
          rightSlot={toolbarRightSlot}
          activeFilterCount={activeFilterCount}
          onClearAllFilters={clearAllFilters}
          allColumns={columns}
          hiddenColumnIds={hiddenColumnIds}
          onToggleColumnVisibility={toggleColumnVisibility}
        />
      ) : null}

      <div
        className={cn('relative w-full', maxHeight ? 'overflow-auto' : 'overflow-x-auto')}
        style={maxHeight ? { maxHeight } : undefined}
      >
        {dragEnabled ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full caption-bottom text-[13px] text-[var(--color-text-primary)] border-collapse">
              {tableHead}
              {tableBody}
            </table>
          </DndContext>
        ) : (
          <table className="w-full caption-bottom text-[13px] text-[var(--color-text-primary)] border-collapse">
            {tableHead}
            {tableBody}
          </table>
        )}
      </div>

      {!hidePagination && !isEmpty && !isLoading && !isError ? (
        <DataTablePagination
          page={safePage}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          total={sorted.length}
        />
      ) : null}

      {selectable && bulkActions.length > 0 ? (
        <BulkActionsBar
          selectedCount={selected.size}
          selectedRows={selectedRows}
          actions={bulkActions}
          onClear={clearSelection}
        />
      ) : null}

      {openFilterColId !== null
        ? (() => {
            const col = visibleCols.find((c) => c.id === openFilterColId);
            if (!col?.filter) return null;
            return (
              <FilterPopover
                col={col}
                data={data}
                anchorEl={filterAnchorRef.current}
                current={columnFilters[openFilterColId]}
                onApply={(filter) => {
                  const next = { ...columnFilters };
                  if (filter === null) {
                    delete next[openFilterColId];
                  } else {
                    next[openFilterColId] = filter;
                  }
                  setColumnFilters(next);
                }}
                onClose={() => setOpenFilterColId(null)}
              />
            );
          })()
        : null}
    </div>
  );
}

function SortIcon({ isSorted, dir }: { isSorted: boolean; dir?: SortDir }) {
  if (!isSorted) return <ChevronsUpDown className="size-3 opacity-50" aria-hidden />;
  return dir === 'asc' ? (
    <ChevronUp className="size-3" aria-hidden />
  ) : (
    <ChevronDown className="size-3" aria-hidden />
  );
}

/** Re-export for consumers who want to preset page sizes. */
export { PAGE_SIZES };
